import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Editions.css';

interface Edition {
  id: string;
  year: string;
  editionTitle: string;
  title: string;
  theme: string;
  date: string;
  location: string;
  description: string;
  highlights: string[];
  speakers: Speaker[];
  sessions: Session[];
  gallery: GalleryImage[];
  stats: EditionStats;
  status: 'completed' | 'upcoming';
}

interface Speaker {
  name: string;
  title: string;
  country: string;
  image: string;
  description?: string;
}

interface Session {
  title: string;
  description: string;
  time: string;
  speakers: string[];
  moderator?: string;
}

interface GalleryImage {
  url: string;
  caption: string;
}

interface EditionStats {
  participants: number;
  countries: number;
  sessions: number;
  speakers: number;
}

const Editions: React.FC = () => {
  const { t, i18n } = useTranslation();
  const showAlbanianSubtitles = i18n.language?.startsWith('sq');
  const [selectedEdition, setSelectedEdition] = useState<string>('2025');
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [selectedSpeakerImage, setSelectedSpeakerImage] = useState<{url: string, name: string} | null>(null);
  const [selectedSpeaker, setSelectedSpeaker] = useState<Speaker | null>(null);

  const editions: Edition[] = [
    {
      id: '2025',
      year: '2025',
      editionTitle: t('editions.secondEdition'),
      title: t('editions.second.title'),
      theme: t('editions.second.theme'),
      date: 'November 5th, 2025',
      location: '"Haxhi Zeka" University, Peja, Kosovo',
      description: t('editions.second.description'),
      highlights: [
        t('editions.second.highlights.expanded'),
        t('editions.second.highlights.ai'),
        t('editions.second.highlights.youth'),
        t('editions.second.highlights.global')
      ],
      speakers: [
        { 
          name: 'General Wesley Clark', 
          title: t('editions.second.speakers.clark.title'), 
          country: t('editions.second.speakers.clark.country'), 
          image: '/Peja Peace Forum 2nd Edition/General Wesley Clark.jpg',
          description: t('editions.second.speakers.clark.description')
        },
        { 
          name: 'Prof. Taro Komatsu', 
          title: t('editions.second.speakers.komatsu.title'), 
          country: t('editions.second.speakers.komatsu.country'), 
          image: '/Peja Peace Forum 2nd Edition/Prof. Taro Komatsu.jpg',
          description: t('editions.second.speakers.komatsu.description')
        },
        { 
          name: 'Ms. Alison Sutherland', 
          title: t('editions.second.speakers.sutherland.title'), 
          country: t('editions.second.speakers.sutherland.country'), 
          image: '/Peja Peace Forum 2nd Edition/Ms. Alison Sutherland.jpg',
          description: t('editions.second.speakers.sutherland.description')
        },
        { 
          name: 'Dr. Sc. Ahmet Ismaili', 
          title: t('editions.second.speakers.ismaili.title'), 
          country: t('editions.second.speakers.ismaili.country'), 
          image: '/Peja Peace Forum 2nd Edition/Dr. sc. Ahmet Ismaili.jpg',
          description: t('editions.second.speakers.ismaili.description')
        },
        { 
          name: 'Mrs. Tina Ramirez', 
          title: t('editions.second.speakers.ramirez.title'), 
          country: t('editions.second.speakers.ramirez.country'), 
          image: '/Peja Peace Forum 2nd Edition/Mrs. Tina Ramirez.jpg',
          description: t('editions.second.speakers.ramirez.description')
        },
        { 
          name: 'Dr. Neshad R. Albani', 
          title: t('editions.second.speakers.albani.title'), 
          country: t('editions.second.speakers.albani.country'), 
          image: '/Peja Peace Forum 2nd Edition/Dr. Neshad R. Albani.jpg',
          description: t('editions.second.speakers.albani.description')
        },
        { 
          name: 'Prof. Dr. Armand Krasniqi', 
          title: t('editions.second.speakers.krasniqi.title'), 
          country: t('editions.second.speakers.krasniqi.country'), 
          image: '/Peja Peace Forum 2nd Edition/Prof. Dr. Armand Krasniqi.jpg',
          description: t('editions.second.speakers.krasniqi.description')
        },
        { 
          name: 'Prof. Bashkim Nurboja', 
          title: t('editions.second.speakers.nurboja.title'), 
          country: t('editions.second.speakers.nurboja.country'), 
          image: '/Peja Peace Forum 2nd Edition/Prof. Bashkim Nurboja.jpg',
          description: t('editions.second.speakers.nurboja.description')
        },
        { 
          name: t('editions.second.speakers.documentary.name'), 
          title: t('editions.second.speakers.documentary.title'), 
          country: t('editions.second.speakers.documentary.country'), 
          image: '/Peja Peace Forum 2nd Edition/Documentary Film the road from Pain to Hope.jpg',
          description: t('editions.second.speakers.documentary.description')
        }
      ],
      sessions: [
        { title: t('editions.second.sessions.registration.title'), description: t('editions.second.sessions.registration.description'), time: '09:00 - 09:25', speakers: [] },
        { title: t('editions.second.sessions.neshad.title'), description: t('editions.second.sessions.neshad.description'), time: '09:25 - 09:35', speakers: ['Dr. Neshad R. Albani'] },
        { title: t('editions.second.sessions.armand.title'), description: t('editions.second.sessions.armand.description'), time: '09:35 - 09:45', speakers: ['Prof. Dr. Armand Krasniqi'] },
        { title: t('editions.second.sessions.clarkintro.title'), description: t('editions.second.sessions.clarkintro.description'), time: '09:45 - 09:50', speakers: ['Dr. Neshad R. Albani'] },
        { title: t('editions.second.sessions.clark.title'), description: t('editions.second.sessions.clark.description'), time: '09:50 - 10:00', speakers: ['General Wesley Clark'] },
        { title: t('editions.second.sessions.economic.title'), description: t('editions.second.sessions.economic.description'), time: '10:00 - 10:30', speakers: ['Dr. Sc. Ahmet Ismaili'] },
        { title: t('editions.second.sessions.education.title'), description: t('editions.second.sessions.education.description'), time: '10:30 - 11:00', speakers: ['Prof. Taro Komatsu'] },
        { title: t('editions.second.sessions.coffeebreak.title'), description: t('editions.second.sessions.coffeebreak.description'), time: '11:00 - 11:30', speakers: [] },
        { title: t('editions.second.sessions.rotary.title'), description: t('editions.second.sessions.rotary.description'), time: '11:30 - 12:00', speakers: ['Ms. Alison Sutherland'] },
        { title: t('editions.second.sessions.documentary.title'), description: t('editions.second.sessions.documentary.description'), time: '12:00 - 12:30', speakers: [] },
        { title: t('editions.second.sessions.humanrights.title'), description: t('editions.second.sessions.humanrights.description'), time: '12:30 - 13:00', speakers: ['Mrs. Tina Ramirez'] },
        { title: t('editions.second.sessions.closing.title'), description: t('editions.second.sessions.closing.description'), time: '13:00 - 13:15', speakers: ['Prof. Bashkim Nurboja'] },
        { title: t('editions.second.sessions.lunch.title'), description: t('editions.second.sessions.lunch.description'), time: '13:30 - 14:30', speakers: [] }
      ],
      gallery: [
        { url: '/Peja Peace Forum 2nd Edition/Peja Peace Forum 2nd Edition.jpg', caption: t('editions.second.gallery.mainAnnouncement') },
        { url: '/Peja Peace Forum 2nd Edition/All speakers.jpg', caption: t('editions.second.gallery.allSpeakers') },
        { url: '/Peja Peace Forum 2nd Edition/Dr. Neshad R. Albani.jpg', caption: 'Dr. Neshad R. Albani - Founder, Peja Peace Forum' },
        { url: '/Peja Peace Forum 2nd Edition/Prof. Dr. Armand Krasniqi.jpg', caption: 'Prof. Dr. Armand Krasniqi - Rector, Haxhi Zeka University' },
        { url: '/Peja Peace Forum 2nd Edition/General Wesley Clark.jpg', caption: 'General Wesley Clark - Former NATO Supreme Allied Commander' },
        { url: '/Peja Peace Forum 2nd Edition/Dr. sc. Ahmet Ismaili.jpg', caption: 'Dr. Sc. Ahmet Ismaili - Governor, Central Bank of Kosovo' },
        { url: '/Peja Peace Forum 2nd Edition/Prof. Taro Komatsu.jpg', caption: 'Prof. Taro Komatsu - Sophia University, Tokyo' },
        { url: '/Peja Peace Forum 2nd Edition/Ms. Alison Sutherland.jpg', caption: 'Ms. Alison Sutherland - Rotary Action Group for Peace Director' },
        { url: '/Peja Peace Forum 2nd Edition/Mrs. Tina Ramirez.jpg', caption: 'Mrs. Tina Ramirez - Hardwired Global President' },
        { url: '/Peja Peace Forum 2nd Edition/Prof. Bashkim Nurboja.jpg', caption: 'Prof. Bashkim Nurboja - Rotary Club Peja President 2025 - 2026' },
        { url: '/Peja Peace Forum 2nd Edition/Documentary Film the road from Pain to Hope.jpg', caption: t('editions.second.gallery.documentary') }
      ],
      stats: {
        participants: 200,
        countries: 15,
        sessions: 13,
        speakers: 9
      },
      status: 'completed'
    },
    {
      id: '2024',
      year: '2024',
      editionTitle: t('editions.firstEdition'),
      title: t('editions.first.title'),
      theme: t('editions.first.theme'),
      date: 'September 11, 2024',
      location: '"Haxhi Zeka" University, Peja, Kosovo',
      description: t('editions.first.description'),
      highlights: [
        t('editions.first.highlights.international'),
        t('editions.first.highlights.panels'),
        t('editions.first.highlights.networking'),
        t('editions.first.highlights.media')
      ],
      speakers: [
        { name: 'Opening Speeches', title: 'Forum Introduction & Welcome', country: 'Opening', image: '/Opening speeches.jpg' },
        { name: 'First Panel', title: 'Human Rights and Peace Education', country: '', image: '/First Panel - Human Rights and Peace Education.jpg' },
        { name: 'Second Panel', title: 'Artificial Intelligence, Media and Peace', country: '', image: '/Second Panel - Artificial Intelligence, Media and Peace.jpg' },
        { name: 'Third Panel', title: 'Peace and Economic Development', country: '', image: '/Third Panel - Peace and Economic Development.jpg' },
        { name: 'Fourth Panel', title: 'Peacebuilding and Conflict Prevention', country: '', image: '/Fourth Panel - Peacebuilding and Conflict Prevention.jpg' }
      ],
      sessions: [
        {
          title: t('editions.first.sessions.humanrights.title'),
          description: t('editions.first.sessions.humanrights.description'),
          time: '09:30 - 10:10',
          speakers: ['Mrs. Tina Ramirez', 'Prof. Yuko Ikuta', 'Mr. Ed Hammel', 'Dr. Tilman Floehr'],
          moderator: 'Prof. Dr. Bashkim Nurboja'
        },
        {
          title: t('editions.first.sessions.ai.title'),
          description: t('editions.first.sessions.ai.description'),
          time: '10:15 - 10:55',
          speakers: ['Mrs. Aida Sofic-Salihbegovic', 'Mr. Daniel Häseker', 'Mr. Muhamet Hajrullahu'],
          moderator: 'Mrs. Celina Begolli'
        },
        {
          title: t('editions.first.sessions.economic.title'),
          description: t('editions.first.sessions.economic.description'),
          time: '11:25 - 12:05',
          speakers: ['Prof. Dr. Bashkim Nurboja', 'Prof. Enton Duro', 'Mr. Mykola Stebjanko'],
          moderator: 'Mr. Denis Gafuri'
        },
        {
          title: t('editions.first.sessions.rotary.title'),
          description: t('editions.first.sessions.rotary.description'),
          time: '12:10 - 12:50',
          speakers: ['Mr. Glenn Miller', 'Prof. Simona Pinton', 'Prof. Marcel Van Opstal'],
          moderator: 'Mr. Ilir Albani'
        }
      ],
      gallery: [
        { url: '/First Edition photos/20240911_091117.jpg', caption: t('editions.first.gallery.photo1') },
        { url: '/First Edition photos/20240911_091917.jpg', caption: t('editions.first.gallery.photo2') },
        { url: '/First Edition photos/20240911_092626.jpg', caption: t('editions.first.gallery.photo3') },
        { url: '/First Edition photos/20240911_093113.jpg', caption: t('editions.first.gallery.photo4') },
        { url: '/First Edition photos/20240911_093152.jpg', caption: t('editions.first.gallery.photo5') },
        { url: '/First Edition photos/20240911_093401.jpg', caption: t('editions.first.gallery.photo6') },
        { url: '/First Edition photos/20240911_094917.jpg', caption: t('editions.first.gallery.photo7') },
        { url: '/First Edition photos/20240911_094928.jpg', caption: t('editions.first.gallery.photo8') },
        { url: '/First Edition photos/20240911_095920.jpg', caption: t('editions.first.gallery.photo9') },
        { url: '/First Edition photos/20240911_100047.jpg', caption: t('editions.first.gallery.photo10') },
        { url: '/First Edition photos/20240911_100050.jpg', caption: t('editions.first.gallery.photo11') },
        { url: '/First Edition photos/20240911_100211.jpg', caption: t('editions.first.gallery.photo12') },
        { url: '/First Edition photos/20240911_100217.jpg', caption: t('editions.first.gallery.photo13') },
        { url: '/First Edition photos/20240911_100224.jpg', caption: t('editions.first.gallery.photo14') },
        { url: '/First Edition photos/20240911_100227.jpg', caption: t('editions.first.gallery.photo15') },
        { url: '/First Edition photos/20240911_100316.jpg', caption: t('editions.first.gallery.photo16') },
        { url: '/First Edition photos/20240911_100328.jpg', caption: t('editions.first.gallery.photo17') },
        { url: '/First Edition photos/20240911_104946.jpg', caption: t('editions.first.gallery.photo18') },
        { url: '/First Edition photos/20240911_105007.jpg', caption: t('editions.first.gallery.photo19') },
        { url: '/First Edition photos/20240911_105013.jpg', caption: t('editions.first.gallery.photo20') },
        { url: '/First Edition photos/20240911_105630.jpg', caption: t('editions.first.gallery.photo21') },
        { url: '/First Edition photos/20240911_105943.jpg', caption: t('editions.first.gallery.photo22') },
        { url: '/First Edition photos/20240911_110039.jpg', caption: t('editions.first.gallery.photo23') },
        { url: '/First Edition photos/20240911_112446.jpg', caption: t('editions.first.gallery.photo24') },
        { url: '/First Edition photos/20240911_112611.jpg', caption: t('editions.first.gallery.photo25') },
        { url: '/First Edition photos/20240911_112819.jpg', caption: t('editions.first.gallery.photo26') },
        { url: '/First Edition photos/20240911_112824.jpg', caption: t('editions.first.gallery.photo27') },
        { url: '/First Edition photos/20240911_112831.jpg', caption: t('editions.first.gallery.photo28') },
        { url: '/First Edition photos/20240911_114214.jpg', caption: t('editions.first.gallery.photo29') },
        { url: '/First Edition photos/20240911_120627.jpg', caption: t('editions.first.gallery.photo30') },
        { url: '/First Edition photos/20240911_120948.jpg', caption: t('editions.first.gallery.photo31') },
        { url: '/First Edition photos/20240911_123121.jpg', caption: t('editions.first.gallery.photo32') },
        { url: '/First Edition photos/20240911_141331.jpg', caption: t('editions.first.gallery.photo33') },
        { url: '/First Edition photos/20240911_141339.jpg', caption: t('editions.first.gallery.photo34') }
      ],
      stats: {
        participants: 150,
        countries: 9,
        sessions: 4,
        speakers: 20
      },
      status: 'completed'
    }
  ];

  const currentEdition = editions.find(e => e.id === selectedEdition) || editions[0];

  return (
    <div className="editions-page">
      {/* Hero Section */}
      <section className="editions-hero">
        <div className="container">
          <h1>{t('editions.hero.title')}</h1>
          <p>{t('editions.hero.subtitle')}</p>
        </div>
      </section>

      {/* Edition Selector */}
      <section className="edition-selector section">
        <div className="container">
          <div className="edition-tabs">
            {editions.map((edition) => (
              <button
                key={edition.id}
                className={`edition-tab ${selectedEdition === edition.id ? 'active' : ''}`}
                onClick={() => setSelectedEdition(edition.id)}
              >
                <span className="edition-year">{edition.year}</span>
                <span className="edition-year-subtitle">{edition.editionTitle}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Edition Details */}
      <section className="edition-details section">
        <div className="container">
          {/* Edition Image */}
          <div className="edition-image">
            <img 
              key={`edition-poster-${currentEdition.id}`}
              src={currentEdition.id === '2024' ? '/Peja Peace Forum 1st Edition.jpg' : '/Peja Peace Forum 2nd Edition/Peja Peace Forum 2nd Edition.jpg'} 
              alt={`${currentEdition.title} - ${currentEdition.theme}`}
              className="edition-poster"
            />
          </div>
          
          <div className="edition-header">
            <div className="edition-info">
              <h2>{currentEdition.title}</h2>
              <h3 className="edition-theme">"{currentEdition.theme}"</h3>
              <div className="edition-meta">
                <div className="meta-item">
                  <i className="icon-calendar"></i>
                  <span>{currentEdition.date}</span>
                </div>
                <div className="meta-item">
                  <i className="icon-location"></i>
                  <span>{currentEdition.location}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="edition-content">
            <div className="edition-description">
              <p>{currentEdition.description}</p>
            </div>

            {currentEdition.id === '2025' && (
              <div className="edition-keynote">
                <h3>{t('editions.second.keynote.title')}</h3>
                <p>{t('editions.second.keynote.description')}</p>
                <div className="edition-keynote-video">
                  <video
                    key={`keynote-${showAlbanianSubtitles ? 'sq' : 'en'}`}
                    controls
                    className="video-player"
                    poster="/Peja Peace Forum 2nd Edition/General Wesley Clark.jpg"
                    preload="metadata"
                    crossOrigin="anonymous"
                  >
                    <source src="/videos/wesley-clark-keynote.mp4" type="video/mp4" />
                    {showAlbanianSubtitles && (
                      <track
                        kind="subtitles"
                        src="/videos/wesley-clark-keynote.sq.vtt"
                        srcLang="sq"
                        label="Shqip"
                        default
                      />
                    )}
                    {t('video.browserNotSupported')}
                  </video>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Agenda */}
      <section className="edition-agenda section">
        <div className="container">
          <h3>{t('editions.agenda.title')}</h3>
          <div className="timeline">
            {currentEdition.sessions.map((session, index) => (
              <div key={`${currentEdition.id}-session-${index}`} className="timeline-item">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <div className="session-time">{session.time}</div>
                  <h4>{session.title}</h4>
                  <p>{session.description}</p>
                  {session.speakers.length > 0 && (
                    <div className="session-speakers">
                      <strong>{session.speakers.length === 1 ? t('editions.speaker.title') : t('editions.speakers.title')}: </strong>
                      {session.speakers.join(', ')}
                    </div>
                  )}
                  {session.moderator && (
                    <div className="session-moderator">
                      <strong>Moderator: </strong>
                      {session.moderator}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Speakers */}
      <section className="edition-speakers section">
        <div className="container">
          <h3>{currentEdition.id === '2024' ? t('editions.panels.first.title') : t('editions.speakers.title')}</h3>
          <div className="speakers-grid">
            {currentEdition.speakers.map((speaker, index) => (
              <div key={`${currentEdition.id}-speaker-${index}`} className="speaker-card">
                {speaker.image && (
                  <div 
                    className="speaker-image"
                    onClick={() => setSelectedSpeakerImage({url: speaker.image, name: speaker.name})}
                  >
                    <img 
                      key={`${currentEdition.id}-speaker-img-${index}`}
                      src={speaker.image} 
                      alt={speaker.name}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                  <div className="speaker-info">
                    <h4>{speaker.name}</h4>
                    <p className="speaker-title">{speaker.title}</p>
                    {speaker.country && (
                      <p className="speaker-country">{speaker.country}</p>
                    )}
                    {speaker.description && (
                      <button 
                        className="learn-more-btn"
                        onClick={() => setSelectedSpeaker(speaker)}
                      >
                        {t('editions.speakers.learnMore')}
                      </button>
                    )}
                  </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {currentEdition.gallery.length > 0 && (
        <section className="edition-gallery section">
          <div className="container">
            <h3>{t('editions.gallery.title')}</h3>
            <div className="gallery-grid">
              {(currentEdition.id === '2024' ? currentEdition.gallery.slice(0, 8) : currentEdition.gallery).map((image, index) => (
                <div 
                  key={`${currentEdition.id}-gallery-${index}`} 
                  className="gallery-item"
                  onClick={() => setSelectedImage(image)}
                >
                  <img key={`${currentEdition.id}-gallery-img-${index}`} src={image.url} alt={image.caption} />
                  <div className="gallery-overlay">
                    <span>{image.caption}</span>
                  </div>
                </div>
              ))}
            </div>
            {currentEdition.id === '2024' && currentEdition.gallery.length > 8 && (
              <div className="gallery-more-button" style={{textAlign: 'center', marginTop: '2rem'}}>
                <a href="/media#gallery" className="btn btn-secondary">{t('editions.gallery.seeMore')}</a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <div className="modal-content">
            <img src={selectedImage.url} alt={selectedImage.caption} />
            <button className="close-modal" onClick={() => setSelectedImage(null)}>×</button>
          </div>
        </div>
      )}

      {/* Speaker Image Modal */}
      {selectedSpeakerImage && (
        <div className="image-modal" onClick={() => setSelectedSpeakerImage(null)}>
          <div className="modal-content">
            <img src={selectedSpeakerImage.url} alt={selectedSpeakerImage.name} />
            <button className="close-modal" onClick={() => setSelectedSpeakerImage(null)}>×</button>
          </div>
        </div>
      )}

      {/* Speaker Description Modal */}
      {selectedSpeaker && (
        <div className="speaker-modal" onClick={() => setSelectedSpeaker(null)}>
          <div className="speaker-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="speaker-modal-header">
              <img src={selectedSpeaker.image} alt={selectedSpeaker.name} className="speaker-modal-image" />
              <div className="speaker-modal-info">
                <h3>{selectedSpeaker.name}</h3>
                <p className="speaker-modal-title">{selectedSpeaker.title}</p>
                <p className="speaker-modal-country">{selectedSpeaker.country}</p>
              </div>
              <button className="close-modal" onClick={() => setSelectedSpeaker(null)}>×</button>
            </div>
            <div className="speaker-modal-description">
              <p>{selectedSpeaker.description}</p>
            </div>
          </div>
        </div>
      )}

      {/* Call to Action */}
      {currentEdition.status === 'upcoming' && (
        <section className="edition-cta section">
          <div className="container">
            <div className="cta-content">
              <h3>{t('editions.cta.title')}</h3>
              <p>{t('editions.cta.description')}</p>
              <a href="/registration" className="btn btn-primary">
                {t('editions.cta.button')}
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Editions;
